import { useDebugDiagnostics } from '@/app/providers/labs/diagnostics'
import type {
  PageLifecycleDiagnostics,
  PagePerformanceDiagnostics
} from '@/app/providers/labs/runtimeDiagnostics'

type RuntimeDiagnosticsModules = {
  streamRegistry: typeof import('@/streams/registry')['streamRegistry']
  rfqPreQuoteScheduler: typeof import(
    '@/app/services/rfqPreQuoteScheduler'
  )['rfqPreQuoteScheduler']
  marketsSummaryScheduler: typeof import(
    '@/app/services/marketsSummaryScheduler'
  )['marketsSummaryScheduler']
  PageLifecycleDiagnostics: typeof import(
    '@/app/providers/labs/runtimeDiagnostics'
  )['PageLifecycleDiagnostics']
  PagePerformanceDiagnostics: typeof import(
    '@/app/providers/labs/runtimeDiagnostics'
  )['PagePerformanceDiagnostics']
  getMarketStreamBrokerActiveSubscriptions: typeof import(
    '@/store/derivative/streamBroker'
  )['getMarketStreamBrokerActiveSubscriptions']
}

export default defineNuxtPlugin(() => {
  const route = useRoute()
  const diagnostics = useDebugDiagnostics()
  let lifecycleDiagnostics: undefined | PageLifecycleDiagnostics
  let performanceDiagnostics: undefined | PagePerformanceDiagnostics
  let runtimeDiagnosticsModules: undefined | RuntimeDiagnosticsModules
  let runtimeDiagnosticsModulesPromise:
    | undefined
    | Promise<RuntimeDiagnosticsModules>
  let runtimeDiagnosticsStartToken = 0

  async function startRuntimeDiagnostics() {
    const startToken = ++runtimeDiagnosticsStartToken
    const modules = await getRuntimeDiagnosticsModules()

    if (
      !diagnostics.isEnabled.value ||
      startToken !== runtimeDiagnosticsStartToken
    ) {
      return
    }

    if (!lifecycleDiagnostics) {
      lifecycleDiagnostics = new modules.PageLifecycleDiagnostics({
        windowSource: window,
        documentSource: document
      })
    }

    if (!performanceDiagnostics) {
      performanceDiagnostics = new modules.PagePerformanceDiagnostics({
        timeOrigin: performance.timeOrigin,
        getContext: getRuntimeDiagnosticsContext,
        observerConstructor:
          typeof PerformanceObserver === 'undefined'
            ? undefined
            : PerformanceObserver
      })
    }

    lifecycleDiagnostics.start()
    performanceDiagnostics.start()
  }

  function stopRuntimeDiagnostics() {
    runtimeDiagnosticsStartToken++
    lifecycleDiagnostics?.stop()
    performanceDiagnostics?.stop()
  }

  function getRuntimeDiagnosticsContext() {
    if (!runtimeDiagnosticsModules) {
      return {
        route: route.fullPath,
        location: window.location.pathname + window.location.search
      }
    }

    return {
      route: route.fullPath,
      location: window.location.pathname + window.location.search,
      activeStreams:
        runtimeDiagnosticsModules.streamRegistry.getActiveStreams(),
      activeSchedulerTasks: [
        ...runtimeDiagnosticsModules.rfqPreQuoteScheduler.getActiveTasks(),
        ...runtimeDiagnosticsModules.marketsSummaryScheduler.getActiveTasks()
      ],
      activeMarketStreamSubscriptions:
        runtimeDiagnosticsModules.getMarketStreamBrokerActiveSubscriptions()
    }
  }

  function getRuntimeDiagnosticsModules() {
    if (runtimeDiagnosticsModules) {
      return Promise.resolve(runtimeDiagnosticsModules)
    }

    if (runtimeDiagnosticsModulesPromise) {
      return runtimeDiagnosticsModulesPromise
    }

    runtimeDiagnosticsModulesPromise = Promise.all([
      import('@/streams/registry'),
      import('@/app/services/rfqPreQuoteScheduler'),
      import('@/app/services/marketsSummaryScheduler'),
      import('@/app/providers/labs/runtimeDiagnostics'),
      import('@/store/derivative/streamBroker')
    ]).then(
      ([
        registryModule,
        rfqPreQuoteSchedulerModule,
        marketsSummarySchedulerModule,
        runtimeDiagnosticsModule,
        streamBrokerModule
      ]) => {
        runtimeDiagnosticsModules = {
          streamRegistry: registryModule.streamRegistry,
          rfqPreQuoteScheduler:
            rfqPreQuoteSchedulerModule.rfqPreQuoteScheduler,
          marketsSummaryScheduler:
            marketsSummarySchedulerModule.marketsSummaryScheduler,
          PageLifecycleDiagnostics:
            runtimeDiagnosticsModule.PageLifecycleDiagnostics,
          PagePerformanceDiagnostics:
            runtimeDiagnosticsModule.PagePerformanceDiagnostics,
          getMarketStreamBrokerActiveSubscriptions:
            streamBrokerModule.getMarketStreamBrokerActiveSubscriptions
        }

        return runtimeDiagnosticsModules
      }
    ).catch((error) => {
      runtimeDiagnosticsModulesPromise = undefined

      throw error
    })

    return runtimeDiagnosticsModulesPromise
  }

  watch(
    diagnostics.isEnabled,
    (isEnabled) => {
      if (!isEnabled) {
        stopRuntimeDiagnostics()

        return
      }

      startRuntimeDiagnostics().catch(() => undefined)
    },
    { immediate: true }
  )
})
